import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { IconWifi } from '@tabler/icons-react';
import { useI18n } from '../locales/i18n-context';
import { Offline } from '../features/errors/Offline';

interface OfflineContextValue {
  offline: boolean;
  setOffline: (v: boolean) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const OfflineContext = createContext<OfflineContextValue>({ offline: false, setOffline: () => {} });

export function useOfflineContext() {
  return useContext(OfflineContext);
}

export interface OfflineProviderProps {
  readonly children?: React.ReactNode;
}

/**
 * OfflineProvider monitors the browser's online/offline status and exposes
 * the `offline` state via context. Pair with <OfflineDetector> to show the UI.
 */
export function OfflineProvider({ children }: OfflineProviderProps) {
  const [offline, setOffline] = useState(!navigator.onLine);
  const { t } = useI18n();

  useEffect(() => {
    const handleOffline = () => setOffline(true);
    const handleOnline = () => {
      setOffline(false);
      notifications.show({
        message: t('error.offline.autoRestore'),
        color: 'green',
        icon: <IconWifi size={16} />,
        autoClose: 3000,
      });
    };

    globalThis.addEventListener('offline', handleOffline);
    globalThis.addEventListener('online', handleOnline);
    return () => {
      globalThis.removeEventListener('offline', handleOffline);
      globalThis.removeEventListener('online', handleOnline);
    };
  }, [t]);

  const contextValue = useMemo(() => ({ offline, setOffline }), [offline]);

  return (
    <OfflineContext.Provider value={contextValue}>
      {offline ? <Offline /> : children}
    </OfflineContext.Provider>
  );
}
